import { Fragment } from "react";
import { Link, usePathname } from "@shared/client/navigation";
import { ShieldCheck } from "lucide-react";
import { ConsentSettingsLink } from "@/components/consent/consent-settings-link";
import { LocaleSelect } from "@/components/locale-select";
import { useLocale, useT } from "@shared/client/locale";
import { Card } from "@shared/components/ui/card";
import { legalUrl } from "@shared/lib/legal";

const linkClass = "underline underline-offset-2 hover:text-foreground";

function Legal() {
  const t = useT();
  const locale = useLocale();
  const copy = t.auth.shell.legal;
  const parts = copy.sentence(
    <a className={linkClass} href={legalUrl("terms", locale)}>{copy.terms}</a>,
    <a className={linkClass} href={legalUrl("termsOfSale", locale)}>{copy.termsOfSale}</a>,
    <a className={linkClass} href={legalUrl("privacy", locale)}>{copy.privacy}</a>,
  );
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>{part}</Fragment>
      ))}{" "}
      <ConsentSettingsLink className={`cursor-pointer ${linkClass}`} />
    </>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const t = useT();
  const copy = t.auth.shell;
  const isPrimaryAuth = pathname === "/auth/signup" || pathname === "/auth/login";

  if (isPrimaryAuth) {
    return (
      <main className="grid min-h-screen lg:grid-cols-2">
        <section className="flex flex-col justify-between p-8 lg:p-12">
          <Link className="inline-flex items-center gap-2.5 self-start" href="/" aria-label={copy.homeLabel}>
            <img alt="" className="size-8 object-contain" height={1500} src="/brand/Logo.svg" width={1500} />
            <span className="text-[15px] font-semibold tracking-tight">Hune</span>
          </Link>

          <div className="flex flex-1 flex-col items-center justify-center py-10">
            {children}
            <p className="mt-8 max-w-[420px] text-[12px] leading-relaxed text-muted-foreground">
              <Legal />
            </p>
          </div>

          <LocaleSelect className="self-start" id="auth-locale" />
        </section>

        <aside className="relative hidden flex-col justify-center gap-8 overflow-hidden bg-accent p-12 lg:flex">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(42,73,111,.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(42,73,111,.05)_1px,transparent_1px)] bg-[size:44px_44px]"
          />
          <Card className="relative z-10 gap-5 p-8">
            <div className="flex items-center gap-2.5">
              <img alt="" className="size-7 object-contain" height={1500} src="/brand/Logo.svg" width={1500} />
              <span className="text-[13px] font-semibold tracking-tight">Hune</span>
            </div>
            <blockquote className="m-0 font-serif text-[21px] leading-snug font-normal">{copy.testimonial.quote}</blockquote>
            <footer className="flex items-center gap-3">
              <img
                alt="Thomas B."
                className="size-10 rounded-full object-cover"
                height={160}
                src="/marketing/testimonials/thomas-b.webp"
                width={160}
              />
              <div className="grid gap-0.5">
                <strong className="text-[12.5px] font-semibold">Thomas B.</strong>
                <span className="text-[12px] text-muted-foreground">{copy.testimonial.role}</span>
              </div>
            </footer>
          </Card>
          <p className="relative z-10 m-0 flex items-center gap-2 text-[12px] text-primary">
            <ShieldCheck className="size-4" /> {copy.sourcesPromise}
          </p>
        </aside>
      </main>
    );
  }

  return (
    <main className="grid min-h-screen lg:grid-cols-[0.85fr_1.15fr]">
      <section className="hidden flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <Link className="inline-flex items-center gap-2.5 self-start" href="/" aria-label={copy.homeLabel}>
          <span className="grid size-8 place-items-center rounded-full bg-primary-foreground font-serif text-[20px] text-primary">
            H
          </span>
          <span className="text-[15px] font-semibold tracking-tight">Hune</span>
        </Link>
        <div className="grid gap-4">
          <p className="m-0 max-w-[420px] font-serif text-[26px] leading-snug font-normal">{copy.pitch}</p>
        </div>
        <p className="m-0 flex items-center gap-2 text-[12px] text-primary-foreground/75">
          <ShieldCheck className="size-4" /> {copy.privacyPromise}
        </p>
      </section>

      <section className="flex flex-col items-center justify-center gap-8 p-8 lg:p-12">
        <Link className="inline-flex items-center gap-2.5 self-start lg:hidden" href="/">
          <img alt="" className="size-8 object-contain" height={1500} src="/brand/Logo.svg" width={1500} />
          <span className="text-[15px] font-semibold tracking-tight">Hune</span>
        </Link>
        {children}
        <div className="grid w-full max-w-[420px] gap-5">
          <p className="m-0 text-[12px] leading-relaxed text-muted-foreground">
            <Legal />
          </p>
          <LocaleSelect id="auth-locale" />
        </div>
      </section>
    </main>
  );
}
